<script setup lang="ts">
import { EMAIL_VERIFICATION_RESEND_COOLDOWN_SECONDS } from '@ruminati/constants'
import { notification } from 'ant-design-vue'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { SyncOutlined } from '@ant-design/icons-vue'

import ExternalLayout from '@/layouts/ExternalLayout.vue'

import { useLogout, useSendVerificationEmail } from '@/composables/auth'
import { useNewTimestampEveryPeriod } from '@/composables/forms'
import { useUser } from '@/composables/user'

const router = useRouter()

const { logout } = useLogout()
const { isSendingVerificationEmail, sendVerificationEmail } = useSendVerificationEmail()
const { profile, isFetchingProfile } = useUser()
const { timestamp } = useNewTimestampEveryPeriod(1000)

// The onboarding request sends the first email before this page is entered.
const resendAvailableAt = ref(Date.now() + EMAIL_VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000)
const resendCooldownSeconds = computed(() =>
  Math.max(0, Math.ceil((resendAvailableAt.value - timestamp.value) / 1000)),
)
const isResendCooldownActive = computed(() => resendCooldownSeconds.value > 0)

const isSendingEmailDisabled = computed(() => {
  return !profile.value || isSendingVerificationEmail.value || isResendCooldownActive.value
})

async function handleLogout() {
  const result = await logout()
  if (result.success) await router.push('/login')
}

async function handleSendVerificationEmail() {
  // N.B. profile.value should be present, but possible that user loads this page as first page load in app, and user attempts to send verification email before profile is loaded
  if (!profile.value || isSendingEmailDisabled.value) return

  resendAvailableAt.value = Date.now() + EMAIL_VERIFICATION_RESEND_COOLDOWN_SECONDS * 1000
  const result = await sendVerificationEmail({ email: profile.value.email })
  if (!result.success) {
    const error = result.error
    const errorCode =
      typeof error === 'object' && error !== null && 'code' in error ? error.code : error
    const context =
      typeof error === 'object' && error !== null && 'context' in error ? error.context : undefined
    const retryAfterSeconds =
      typeof context === 'object' &&
      context !== null &&
      'retryAfterSeconds' in context &&
      typeof context.retryAfterSeconds === 'number'
        ? Math.ceil(context.retryAfterSeconds)
        : EMAIL_VERIFICATION_RESEND_COOLDOWN_SECONDS

    if (errorCode === 'RATE_LIMITED') {
      resendAvailableAt.value = Date.now() + retryAfterSeconds * 1000
      notification.warning({
        message: 'Please wait before resending',
        description: `Please wait ${retryAfterSeconds} seconds before requesting another verification email.`,
      })
    } else {
      notification.error({
        message: 'Failed to send verification email',
        description:
          'Something went wrong while attempting to send the verification email. Please try again later.',
      })
    }
  }
}
</script>

<template>
  <ExternalLayout>
    <div class="flex flex-col gap-16">
      <div class="flex flex-col">
        <h2>Verify your email address</h2>
        <p class="text-rumi-grey-650 text-sm">
          We've sent you an email with a verification link. Please check your inbox, and click the
          link to verify your email address and continue.
        </p>
      </div>
      <div
        class="b-solid b-1 b-rumi-grey-500 border-rd-lg bg-rumi-grey-300 flex flex-col gap-2 p-4"
      >
        <p class="text-rumi-grey-650 text-sm">
          Didn't get the email? Check your spam folder, or click resend below.
        </p>
        <a-flex>
          <AButton :disabled="isSendingEmailDisabled" @click="handleSendVerificationEmail">
            <sync-outlined v-if="!profile || isSendingVerificationEmail" spin />
            {{
              isFetchingProfile
                ? 'Loading...'
                : isSendingVerificationEmail
                  ? 'Sending...'
                  : isResendCooldownActive
                    ? `Resend available in ${resendCooldownSeconds}s`
                    : 'Resend'
            }}
          </AButton>
        </a-flex>
      </div>
      <div class="flex flex-col gap-2">
        <p class="text-rumi-grey-600 text-sm">
          Not sure why you're here?
          <span class="text-rumi-jungle-green-700 cursor-pointer underline" @click="handleLogout">
            Return to login.
          </span>
        </p>
      </div>
    </div>
  </ExternalLayout>
</template>
